import { motion } from "framer-motion";
import { ArrowRight, FileText, ShieldCheck, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import InfrastructureConsole from "@/components/InfrastructureConsole";

const BADGES = [
    "70/30 Hybrid Compute",
    "AI Workload Routing",
    "Encrypted Storage",
    "On-Chain Settlement",
];

const STATS = [
    { v: "70/30",  l: "Hybrid Model" },
    { v: "<3s",    l: "Finality" },
    { v: "EVM",    l: "Settlement" },
    { v: "5%",     l: "Developer Rev Share" },
];

export default function Hero() {
    return (
        <section className="relative pt-24 sm:pt-32 md:pt-40 pb-16 sm:pb-20 md:pb-28 overflow-hidden" data-testid="hero-section">
            <div className="absolute inset-0 hero-glow-bg pointer-events-none" />
            <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                {/* LEFT — narrative */}
                <div className="lg:col-span-6 relative z-10 lg:pt-6">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6 sm:mb-8 max-w-full"
                        data-testid="hero-badge-patent"
                    >
                        <ShieldCheck size={13} className="text-brand-cyan shrink-0" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-2 truncate">
                            Patent Filed · Hybrid-Decentralized Cloud Protocol
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-heading font-black text-[2rem] sm:text-[2.6rem] md:text-[2.9rem] lg:text-[3.2rem] xl:text-[3.5rem] leading-[1.08] tracking-[-0.025em] text-ink"
                        data-testid="hero-headline"
                    >
                        <span className="block">Cloud infrastructure</span>
                        <span className="block">
                            for <span className="brand-gradient-text">Internet 3.0</span>
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-6 sm:mt-8 text-[15px] sm:text-base md:text-lg text-muted-ink max-w-xl leading-[1.65] sm:leading-[1.7]"
                        data-testid="hero-subheadline"
                    >
                        DeCloudX combines decentralized compute, AI workloads, encrypted storage, and Layer-1 settlement into one resilient cloud protocol.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.45 }}
                        className="mt-7 flex flex-wrap gap-2"
                        data-testid="hero-badges"
                    >
                        {BADGES.map((b, i) => <span key={i} className="chip">{b}</span>)}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55 }}
                        className="mt-10 flex flex-wrap items-center gap-3"
                    >
                        <Link to="/contact" className="group btn-primary" data-testid="hero-cta-join">
                            Join Network
                            <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link to="/network" className="btn-secondary" data-testid="hero-cta-protocol">
                            <Workflow size={15} strokeWidth={1.5} className="text-brand-cyan" />
                            Explore Protocol
                        </Link>
                        <Link to="/whitepaper" className="btn-ghost" data-testid="hero-cta-whitepaper">
                            <FileText size={15} strokeWidth={1.5} />
                            Read Whitepaper
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        className="mt-10 sm:mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 max-w-xl"
                    >
                        {STATS.map((s, i) => (
                            <div key={i} data-testid={`hero-stat-${i}`} className="min-w-0">
                                <div className="font-heading font-bold text-lg sm:text-xl md:text-2xl text-ink tracking-tight">{s.v}</div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.12em] sm:tracking-[0.18em] text-faint-ink mt-1 break-words">{s.l}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* RIGHT — Protocol Console */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="lg:col-span-6 relative z-10"
                    data-testid="hero-cloud-core"
                >
                    <InfrastructureConsole />
                </motion.div>
            </div>
        </section>
    );
}
